import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShieldCheck, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import PageTitle from '../components/PageTitle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import SpellCheckDialog from '../components/SpellCheckDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { value: 'feature', label: 'Feature' },
  { value: 'bug', label: 'Bug' },
  { value: 'ux', label: 'UX / Design' },
  { value: 'integration', label: 'Integration' },
  { value: 'other', label: 'Other' },
];

const Moderation = () => {
  const { user } = useAuth();
  const isStaff = isStaffOrAbove(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reasons, setReasons] = useState({});       // {id: reason text}
  const [categories, setCategories] = useState({}); // {id: category override}
  // Spell-check state when an admin rejects with a written reason.
  // We only intercept the 'rejected' path because that's the only place
  // admin-authored prose hits the suggester's inbox.
  const [pendingModeration, setPendingModeration] = useState(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/suggestions/pending`, { withCredentials: true });
      setItems(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load moderation queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isStaff) fetchPending(); }, [fetchPending, isStaff]);

  if (!isStaff) return <Navigate to="/home" replace />;

  const doModerate = async (item, payload) => {
    setBusyId(item.suggestion_id);
    try {
      await axios.patch(
        `${API}/suggestions/${item.suggestion_id}`,
        payload,
        { withCredentials: true },
      );
      toast.success(payload.status === 'rejected' ? 'Suggestion rejected (author notified)' : 'Suggestion approved and live');
      setItems((prev) => prev.filter((x) => x.suggestion_id !== item.suggestion_id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const moderate = async (item, status) => {
    const payload = {
      status,
      category: categories[item.suggestion_id] || item.category,
      rejection_reason: status === 'rejected' ? (reasons[item.suggestion_id] || '').trim() || null : null,
      notify_author: true,
    };
    // Only spell-check admin-authored prose (rejection reasons sent to the user).
    if (status === 'rejected' && payload.rejection_reason) {
      try {
        const r = await axios.post(
          `${API}/admin/spell-check`,
          { payload: payload.rejection_reason },
          { withCredentials: true },
        );
        if (r.data?.total_issues > 0) {
          setPendingModeration({ item, payload, findings: r.data.findings });
          return;
        }
      } catch (err) {
        console.warn('Spell-check failed, moderating without it:', err);
      }
    }
    await doModerate(item, payload);
  };

  const applyFixesAndModerate = async () => {
    if (!pendingModeration) return;
    const { item, payload } = pendingModeration;
    try {
      const r = await axios.post(
        `${API}/admin/spell-check/apply`,
        { payload: payload.rejection_reason },
        { withCredentials: true },
      );
      setPendingModeration(null);
      await doModerate(item, { ...payload, rejection_reason: r.data?.payload });
    } catch (err) {
      console.warn('Spell-check apply failed:', err);
    }
  };

  const moderateAsIs = async () => {
    if (!pendingModeration) return;
    const { item, payload } = pendingModeration;
    setPendingModeration(null);
    await doModerate(item, payload);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-12" data-testid="moderation-page">
      <Link to="/home" className="inline-flex items-center text-sm text-[#64748B] hover:text-[#020617] mb-6">
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to home
      </Link>
      <PageTitle
        title="Moderation queue"
        subtitle="Approve to publish on the community board, or reject with an optional reason that's emailed to the author."
        className="mb-8"
      />

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052FF]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
          <p className="text-[#64748B]">Inbox zero — no pending suggestions right now.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <motion.div key={item.suggestion_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={`pending-card-${item.suggestion_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-lg font-semibold text-[#020617]">{item.title}</h3>
                <span className="text-xs text-[#64748B]">{new Date(item.created_at).toLocaleDateString('en-GB')}</span>
              </div>
              {item.description && (<p className="text-sm text-[#475569] mb-4 leading-relaxed whitespace-pre-line">{item.description}</p>)}
              <div className="text-xs text-[#64748B] mb-4">
                from <span className="font-medium text-[#020617]">{item.user_name}</span>
                {item.user_email && <> · <span className="text-[#64748B]">{item.user_email}</span></>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Category</Label>
                  <Select value={categories[item.suggestion_id] || item.category} onValueChange={(v) => setCategories((p) => ({ ...p, [item.suggestion_id]: v }))}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORY_OPTIONS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Rejection reason (optional, shown in email)</Label>
                  <Textarea
                    rows={2}
                    value={reasons[item.suggestion_id] || ''}
                    onChange={(e) => setReasons((p) => ({ ...p, [item.suggestion_id]: e.target.value }))}
                    placeholder="e.g. Out of scope for now; revisiting in 2027."
                    className="mt-1.5"
                    data-testid={`rejection-reason-${item.suggestion_id}`}
                  />
                </div>
              </div>

              <div className="flex gap-3 flex-wrap">
                <Button
                  onClick={() => moderate(item, 'proposed')}
                  disabled={busyId === item.suggestion_id}
                  className="bg-[#10B981] text-white hover:bg-[#059669]"
                  data-testid={`approve-${item.suggestion_id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve & publish
                </Button>
                <Button
                  onClick={() => moderate(item, 'rejected')}
                  disabled={busyId === item.suggestion_id}
                  className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
                  data-testid={`reject-${item.suggestion_id}`}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      <SpellCheckDialog
        open={!!pendingModeration}
        onOpenChange={(open) => { if (!open) setPendingModeration(null); }}
        findings={pendingModeration?.findings || []}
        onApplyAll={applyFixesAndModerate}
        onSaveAsIs={moderateAsIs}
        busy={!!busyId}
      />
    </div>
  );
};

export default Moderation;
